import React, { useState, useRef, useEffect } from 'react';
import { HandDrawnButton } from '../HandDrawnButton/HandDrawnButton';
import './FilterDropdown.css';

export interface FilterOption {
  id: string;
  label: string;
  value: string;
}

export interface FilterDropdownProps {
  type: 'destination' | 'type';
  level?: 'brand' | 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina';
  options: FilterOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  className?: string;
  variant?: 'default' | 'compact';
}

/**
 * FilterDropdown Component - Exact Figma implementation with functional dropdown
 * Matches all states: Default, hover, expanded with proper option selection
 */
export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  type,
  level = 'brand',
  options,
  selectedValue,
  onSelect,
  className = '',
  variant = 'default'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHeaderClick = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOptionSelect = (value: string) => {
    if (value === '') {
      onSelect(''); // Clear selection
    } else {
      onSelect(value);
    }
    setIsExpanded(false);
  };

  const getHeaderText = () => {
    if (type === 'destination') {
      return selectedValue 
        ? options.find(opt => opt.value === selectedValue)?.label || 'FILTER BY DESTINATION'
        : 'FILTER BY DESTINATION';
    } else {
      return selectedValue 
        ? options.find(opt => opt.value === selectedValue)?.label || 'TYPE'
        : 'TYPE';
    }
  };

  const themeClass = level || 'brand';
  const expandedClass = isExpanded ? 'expanded' : '';
  const variantClass = variant === 'compact' ? 'compact' : 'default';

  return (
    <div 
      ref={dropdownRef}
      className={`filter-dropdown filter-dropdown--${themeClass} filter-dropdown--${variantClass} ${expandedClass ? 'filter-dropdown--expanded' : ''} ${className}`}
      data-type={type}
    >
      <HandDrawnButton
        text={`${getHeaderText()} ${isExpanded ? '−' : '+'}`}
        onClick={handleHeaderClick}
        variant={level === 'brand' ? 'brand' : level}
        color="awasi"
        animation="stroke-to-fill"
        size="normal"
        className={`filter-dropdown__header filter-dropdown__header--${variantClass} ${isExpanded ? 'filter-dropdown__header--expanded' : ''}`}
      />

      {isExpanded && (
        <div className="filter-dropdown__options">
          <button
            className="filter-dropdown__option"
            onClick={() => handleOptionSelect('')}
            type="button"
          >
            <span className="filter-dropdown__option-text">
              {type === 'destination' ? 'ALL DESTINATIONS' : 'ALL TYPES'}
            </span>
          </button>
          {options.map((option) => (
            <button
              key={option.id}
              className={`filter-dropdown__option ${selectedValue === option.value ? 'filter-dropdown__option--selected' : ''}`}
              onClick={() => handleOptionSelect(option.value)}
              type="button"
            >
              <span className="filter-dropdown__option-text">
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;